import { useReducer, useRef, useState } from 'react';
import { Device, Call } from '@twilio/voice-sdk';

import { disconnectCall, handleRinging, initDevice, ringingReducer } from './helpers';
import { useBoard } from '../../pages/CaseBoard/useBoard';
import useGetTwilioToken from '../../services/Twilio/useGetTwilioToken';
import usePostCallData from '../../services/Twilio/usePostCallData';
import { OngoingCall } from './types';

const useTwilioLogic = () => {
    const [device, setDevice] = useState<Device>();
    const [ongoingCall, setOngoingCall] = useState<OngoingCall>();
    const [showKeys, setShowKeys] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const { data } = useGetTwilioToken();
    const { caseData, isTwilioWidgetOpen, setIsTwilioWidgetOpen } = useBoard();
    const { mutate: postCallData } = usePostCallData();
    const timerTimeoutId = useRef<NodeJS.Timeout | null>(null);
    const [{ showRingingDots, showIncomingCall, startTimer, showDuration }, dispatch] = useReducer(ringingReducer, {
        showRingingDots: false,
        showIncomingCall: false,
        startTimer: false,
        showDuration: false
    });

    const { phone } = caseData || {};
    const { token } = data || {};

    const handleIncomingCall = (call: Call) => {
        if (!ongoingCall) {
            setOngoingCall({ call, type: 'incoming' });
            setIsTwilioWidgetOpen(true);
            dispatch('showIncomingCall');

            call.on('disconnect', () => disconnectCall(dispatch, call, setOngoingCall));
            call.on('cancel', () => {
                console.log('The call has been cancelled');
                disconnectCall(dispatch, call, setOngoingCall);
            });
            call.on('reject', () => {
                console.log('The call has been rejected');
                disconnectCall(dispatch, call, setOngoingCall);
            });
            call.on('accept', () => {
                console.log('The call has been accepted');
                dispatch('startTimer');
            });
        }
    };

    if (!device && token && phone) initDevice(token, setDevice, handleIncomingCall);

    const handleOutgoingCall = async () => {
        if (phone && device) {
            try {
                const params = { To: `+1${phone}` };
                const call = await device.connect({ params });

                if (call) {
                    setOngoingCall({ call, type: 'outgoing' });
                    call.on('ringing', () => {
                        timerTimeoutId.current = handleRinging(call, phone, dispatch, postCallData);
                    });
                    call.on('accept', () => console.log('accept'));
                    call.on('disconnect', () => disconnectCall(dispatch, call, setOngoingCall, timerTimeoutId.current));
                    call.on('error', (twilioError) => console.log('Error', twilioError));
                    call.on('warning', (warnName, warnData) => console.log('Warning', warnName, warnData));
                    call.on('cancel', () => console.log('cancel'));
                    call.on('reject', () => console.log('reject'));
                    call.on('reconnected', () => console.log('reconnected'));
                    call.on('reconnecting', (error) => console.log('reconnecting', error));
                    call.on('answered', () => console.log('answered'));
                    call.on('connected', () => console.log('connected'));
                }
            } catch (error) {
                throw new Error('Twilio call issue');
            }
        }
    };

    const toggleMuteMicrophone = () => {
        setIsMuted(!isMuted);
        ongoingCall?.call?.mute(!isMuted);
    };

    return {
        caseData,
        ongoingCall,
        handleOutgoingCall,
        showKeys,
        setShowKeys,
        showRingingDots,
        showIncomingCall,
        startTimer,
        showDuration,
        isMuted,
        toggleMuteMicrophone,
        isTwilioWidgetOpen,
        setIsTwilioWidgetOpen
    };
};
export default useTwilioLogic;
